import {MISSION_SCRIPT_KINDS} from '../../../configs/campaigns/missionFormat';

/**
 * Состояние сюжетных миссий — и РЕДАКТОРА (админ записывает миссию), и
 * ПРОХОЖДЕНИЯ (игрок отыгрывает записанное). Слайс один: половина полей
 * пишется только в редакторе, половина — только при прохождении, но живут они
 * рядом, потому что редактор — это то же прохождение, только с записью.
 *
 * Всё чисто клиентское (все экшены noServerSync) и стирается RESET_STATE при
 * выходе из партии — как и обучение.
 */
const initialState = {
  // === Редактор ===
  isEditorMode: false,
  // Черновик шапки миссии: id/название/описание/награда/лимит раундов
  draft: null,
  // Лента записей миссии: действия, реплики, задания, переключения на ИИ
  script: [],
  // Картинки персонажей, загруженные в этой сессии редактора:
  // {id, name, path, dataUrl} — dataUrl только для предпросмотра, в конфиг
  // уходит относительный path
  characterImages: [],

  // === Прохождение ===
  missionId: null,
  // За кого играет живой игрок — по нему evaluateObjectives проверяет задания
  playerColor: null,
  // Указатель на следующую необработанную запись ленты
  scriptIndex: 0,
  // Цвета игроков, за которых дальше ходит обычная система ботов
  autoBotColors: [],
  // Задания, которые уже появились у игрока
  activeObjectives: [],
  completedObjectiveIds: [],
  // Накопленный прогресс условий вида «лидировать N раундов подряд»
  progress: {leadingRounds: 0, lastLeadingRound: null},
  // Реплика, показываемая прямо сейчас (блокирует ход до нажатия «Далее»)
  activeReplica: null,
  isMissionCompleted: false,
  isMissionFailed: false,
};

const missionReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'MISSION_EDITOR_START':
      return {
        ...initialState,
        isEditorMode: true,
        draft: action.payload.draft,
      };

    case 'MISSION_EDITOR_UPDATE_DRAFT':
      return {
        ...state,
        draft: {...(state.draft || {}), ...action.payload.patch},
      };

    case 'MISSION_EDITOR_ADD_CHARACTER_IMAGE':
      return {
        ...state,
        characterImages: [...state.characterImages, action.payload.image],
      };

    // Запись действия в ленту (пишет missionEditorMiddleware)
    case 'MISSION_SCRIPT_APPEND':
      return {
        ...state,
        script: [...state.script, action.payload.entry],
      };

    case 'MISSION_SCRIPT_REMOVE': {
      const script = state.script.filter((entry) => entry.id !== action.payload.entryId);
      return {...state, script};
    }

    case 'MISSION_START':
      return {
        ...initialState,
        missionId: action.payload.missionId,
        script: action.payload.script || [],
        playerColor: action.payload.playerColor || null,
      };

    case 'MISSION_ADVANCE_SCRIPT': {
      // Продвижение только с ожидаемого индекса: повторный диспатч того же
      // шага (двойные эффекты StrictMode) не должен перепрыгивать записи
      const fromIndex = action.payload?.fromIndex;
      if (fromIndex !== null && fromIndex !== undefined && fromIndex !== state.scriptIndex) {
        return state;
      }
      return {...state, scriptIndex: state.scriptIndex + 1};
    }

    case 'MISSION_SET_BOT_AUTO': {
      const {color} = action.payload;
      if (state.autoBotColors.includes(color)) return state;
      return {...state, autoBotColors: [...state.autoBotColors, color]};
    }

    case 'MISSION_SHOW_REPLICA':
      return {...state, activeReplica: action.payload.replica};

    case 'MISSION_CLOSE_REPLICA':
      return {...state, activeReplica: null};

    case 'MISSION_ADD_OBJECTIVE': {
      const {objective} = action.payload;
      if (state.activeObjectives.some((item) => item.id === objective.id)) return state;
      return {...state, activeObjectives: [...state.activeObjectives, objective]};
    }

    // Задания проверяются по состоянию, поэтому список выполненных
    // ПЕРЕСЧИТЫВАЕТСЯ целиком, а не накапливается: отмена действия должна
    // возвращать задание в невыполненные
    case 'MISSION_SET_COMPLETED_OBJECTIVES':
      return {...state, completedObjectiveIds: action.payload.completedObjectiveIds};

    case 'MISSION_SET_PROGRESS':
      return {...state, progress: {...state.progress, ...action.payload.patch}};

    case 'MISSION_FINISHED':
      return {
        ...state,
        isMissionCompleted: true,
        isMissionFailed: Boolean(action.payload.isFailed),
      };

    default:
      return state;
  }
};

// Сколько записей ленты относится к действиям конкретного игрока — редактору
// нужно для подписи «записано ходов»
export const countMissionScriptActions = (script, color) => script.filter((entry) => (
  entry.kind === MISSION_SCRIPT_KINDS.ACTION && (!color || entry.playerColor === color)
)).length;

export default missionReducer;
